import type { BuyAndPrintResult } from "../types";

// What to tell the merchant after a batch. The distinction that matters is
// between a label that was bought and one that was not: an order already
// fulfilled somewhere else produces a successful request in which nothing was
// printed, and calling that "Bought and printed 1 label" sends the merchant
// looking at the printer for something that will never come out.
export interface BuyAndPrintSummary {
  purchased: number;
  skipped: number;
  // Labels re-sent to the printer from postage already paid for. Reported apart
  // from purchases so the merchant is never told they bought something they
  // didn't — these cost nothing.
  reprinted: number;
  tone: "success" | "info";
  message: string;
  // The first reason given, when the whole batch was skipped for one cause.
  detail: string | null;
}

function labels(count: number): string {
  return `${count} label${count === 1 ? "" : "s"}`;
}

function orders(count: number): string {
  return `${count} order${count === 1 ? "" : "s"}`;
}

export function summarizeBuyAndPrint(results: BuyAndPrintResult[]): BuyAndPrintSummary | null {
  const successful = results.filter((result) => result.ok);
  if (successful.length === 0) return null;

  // Older workers answered without the counts. Treat every ok row as a real
  // label then, which is what the banner said before this existed.
  const purchased = successful.reduce((sum, result) => sum + (result.purchased ?? 1), 0);
  const skipped = successful.reduce((sum, result) => sum + (result.skipped ?? 0), 0);
  const reprinted = successful.reduce((sum, result) => sum + (result.reprinted ?? 0), 0);

  if (purchased === 0 && reprinted > 0) {
    // Postage was already paid for; these labels cost nothing to put on paper
    // again. Saying "bought" here would be a lie about the merchant's money.
    return {
      purchased,
      skipped,
      reprinted,
      tone: "success",
      message: `Reprinted ${labels(reprinted)} already paid for · nothing new was bought`,
      detail: null,
    };
  }

  if (purchased === 0) {
    // The reason comes from the worker rather than being reconstructed here,
    // so the banner can only ever say what actually stopped the purchase.
    const reason = successful.find((result) => result.skipReason)?.skipReason ?? null;
    const count = skipped || successful.length;
    return {
      purchased,
      skipped,
      reprinted,
      tone: "info",
      message: reason
        ? `Skipped ${orders(count)} — ${reason.charAt(0).toLowerCase()}${reason.slice(1)}`
        : `Skipped ${orders(count)} — nothing to buy`,
      detail: null,
    };
  }

  const extras = [
    reprinted > 0 ? `${reprinted} reprinted` : null,
    skipped > 0 ? `${skipped} skipped` : null,
  ].filter(Boolean);

  return {
    purchased,
    skipped,
    reprinted,
    tone: "success",
    message:
      extras.length > 0
        ? `Bought and printed ${labels(purchased)} · ${extras.join(" · ")}`
        : `Bought and printed ${labels(purchased)}`,
    detail: skipped > 0 ? successful.find((result) => result.skipReason)?.skipReason ?? null : null,
  };
}
